import { graphql } from 'react-relay';
import { useCallback } from 'react';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useShouldShowDSPWarningForWorkspaceRole } from '@confluence/data-security/entry-points/useShouldShowDSPWarningForWorkspaceRole.ts';
import {
	useMutationInterop,
	type MutationFunctionInterop,
} from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type {
	BulkRoleAssignmentSpaceType,
	Exact,
	InputMaybe,
	RoleAssignmentPrincipalType,
	Scalars,
	useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation as useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation$data,
	useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutationVariables as useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation$variables,
	useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation_Mutation,
} from './__types__/useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation.ts';
import type {
	useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation as useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation$data,
	useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutationVariables as useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation$variables,
	useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation_Mutation,
} from './__types__/useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation.ts';
import { useBulkRoleFlags } from './useBulkRoleFlags.tsx';
const useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation = graphql`
	mutation useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation(
		$principalId: ID!
		$principalType: RoleAssignmentPrincipalType!
		$roleId: ID!
		$spaceTypes: [BulkRoleAssignmentSpaceType]!
	) {
		bulkSetRoleAssignmentToSpaces(
			input: {
				roleAssignment: {
					principal: { principalId: $principalId, principalType: $principalType }
					roleId: $roleId
				}
				spaceTypes: $spaceTypes
			}
		) {
			taskId
			success
			errors {
				message
			}
		}
	}
`;
const useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation = graphql`
	mutation useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation(
		$principalId: ID!
		$principalType: RoleAssignmentPrincipalType!
		$spaceTypes: [BulkRoleAssignmentSpaceType]!
	) {
		bulkRemoveRoleAssignmentFromSpaces(
			input: {
				principal: { principalId: $principalId, principalType: $principalType }
				spaceTypes: $spaceTypes
			}
		) {
			taskId
			success
			errors {
				message
			}
		}
	}
`;
export const useBulkRoleSetAssignment = ({
	closeDialog,
	flags,
	rolePermissions,
}: {
	closeDialog: () => void;
	flags: FlagsStateContainer;
	rolePermissions: string[];
}): {
	bulkSetRoleAssignmentToSpaces: MutationFunctionInterop<
		useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation_Mutation,
		Exact<{
			principalId: Scalars['ID']['input'];
			principalType: RoleAssignmentPrincipalType;
			roleId: Scalars['ID']['input'];
			spaceTypes:
				| Array<InputMaybe<BulkRoleAssignmentSpaceType>>
				| InputMaybe<BulkRoleAssignmentSpaceType>;
		}>
	>;
	bulkRemoveRoleAssignment: MutationFunctionInterop<
		useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation_Mutation,
		Exact<{
			principalId: Scalars['ID']['input'];
			principalType: RoleAssignmentPrincipalType;
			spaceTypes:
				| Array<InputMaybe<BulkRoleAssignmentSpaceType>>
				| InputMaybe<BulkRoleAssignmentSpaceType>;
		}>
	>;
	bulkAssignmentData:
		| useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation_Mutation
		| undefined;
	bulkRemoveAssignmentData:
		| useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation_Mutation
		| undefined;
} => {
	const { showErrorFlag, showProcessingFlag, showInfoFlag } = useBulkRoleFlags({ flags });

	const showDSPWarning = useShouldShowDSPWarningForWorkspaceRole(rolePermissions);

	const onMutationErrored = useCallback(() => {
		closeDialog();
		void showErrorFlag();
	}, [closeDialog, showErrorFlag]);

	const onAsyncMutationSubmitted = useCallback(
		(data) => {
			closeDialog();
			/**
			 * The backend is counting the mutation as a success for some exceptions
			 * but returning an error in the mutation response.  We need to check for
			 * these here.
			 */
			if (
				data?.bulkSetRoleAssignmentToSpaces?.errors?.length > 0 ||
				data?.bulkRemoveRoleAssignmentFromSpaces?.errors?.length > 0
			) {
				onMutationErrored();
			} else {
				if (
					data.bulkSetRoleAssignmentToSpaces?.taskId ||
					data.bulkRemoveRoleAssignmentFromSpaces?.taskId
				) {
					if (showDSPWarning) {
						void showInfoFlag();
					} else {
						// show the progress flag
						void showProcessingFlag();
					}
				}
			}
		},
		[closeDialog, onMutationErrored, showDSPWarning, showInfoFlag, showProcessingFlag],
	);
	const [bulkSetRoleAssignmentToSpaces, { data: bulkAssignmentData }] = useMutationInterop<
		useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation$data,
		useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation$variables
	>(useBulkRoleSetAssignmentBulkSetRoleAssignmentToSpacesMutation, {
		onCompleted: onAsyncMutationSubmitted,
		onError: () => {
			closeDialog();
			onMutationErrored();
		},
	});
	const [bulkRemoveRoleAssignment, { data: bulkRemoveAssignmentData }] = useMutationInterop<
		useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation$data,
		useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation$variables
	>(useBulkRoleSetAssignmentBulkRemoveRoleAssignmentFromSpacesMutation, {
		onCompleted: onAsyncMutationSubmitted,
		onError: () => {
			closeDialog();
			onMutationErrored();
		},
	});
	return {
		bulkSetRoleAssignmentToSpaces,
		bulkRemoveRoleAssignment,
		bulkAssignmentData,
		bulkRemoveAssignmentData,
	};
};
